import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import { Heart, Zap, Users, Coffee } from 'lucide-react';

// Images
import docksImage from '@/media/images/The Docks - Ellie Reid.jpg';


const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const values = [
  {
    icon: Heart,
    title: 'Genuine Care',
    desc: 'We actually care about your business succeeding. Your website isn\'t just another project to us.'
  },
  {
    icon: Zap,
    title: 'No Nonsense',
    desc: 'We skip the jargon and get straight to what matters. Clear communication, honest advice.'
  },
  {
    icon: Users,
    title: 'Local Focus',
    desc: 'We understand local businesses because we are one. We know what works for your market.'
  },
  {
    icon: Coffee,
    title: 'Real Support',
    desc: 'We don\'t disappear after launch. Need help? We\'re just an email or call away.'
  }
];

const About = () => {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
                About Dockside Design
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                We're a small, independent web studio based in the UK. We started Dockside 
                because we saw too many local businesses struggling with outdated websites 
                or getting overcharged by big agencies.
              </p>
              <p className="text-muted-foreground mb-8">
                Our mission is simple: help local businesses look professional online 
                without the hassle, the jargon, or the massive price tags. We believe 
                a good website shouldn't cost a fortune or take months to build.
              </p>
              <Button
                asChild
                data-testid="about-cta-btn"
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
              >
                <Link to="/contact">Get in Touch</Link>
              </Button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                    src={docksImage}
                    alt="Our studio workspace"
                    className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto">
            <motion.div {...fadeInUp}>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
                Our Story
              </h2>
              <div className="space-y-6 text-muted-foreground">
                <p>
                    Dockside Design was built on a simple idea: local businesses shouldn’t have to struggle to get a great website.
                </p>
                <p>
                    We kept seeing the same problem.
                    Brilliant cafés, talented tradespeople, and hardworking business owners stuck with outdated sites — or facing confusing,
                    expensive agency quotes that made the whole process feel overwhelming.
                </p>
                <p>
                    We believed getting online shouldn’t be complicated or intimidating. It should be clear, affordable, and focused on helping businesses grow.
                </p>
                <p>
                    So we built Dockside Design to do exactly that.
                </p>
                <p>
                    Today, we work with businesses across the UK to create modern websites that look professional, load fast, and help turn visitors into real customers.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              What We Believe In
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              These aren't just words on a page — they're how we actually work.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full border-border/50 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <value.icon className="w-7 h-7 text-accent" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">{value.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Local */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Why We Focus on Local
              </h2>
              <p className="text-primary-foreground/80 mb-6">
                Big agencies chase big clients. They want corporate contracts and 
                enterprise deals. That leaves local businesses — the backbone of 
                our communities — underserved.
              </p>
              <p className="text-primary-foreground/80">
                We think that's backwards. Local businesses are the ones that need 
                the most help getting online properly. They're the cafés where you 
                grab your morning coffee, the plumbers who fix your emergency leak, 
                the florists who make your special occasions beautiful. They deserve 
                a proper website too.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="bg-primary-foreground/10 rounded-xl p-6 text-center">
                <p className="font-heading text-4xl font-bold mb-2">4+</p>
                <p className="text-primary-foreground/70 text-sm">Websites Launched</p>
              </div>
              <div className="bg-primary-foreground/10 rounded-xl p-6 text-center">
                <p className="font-heading text-4xl font-bold mb-2">UK</p>
                <p className="text-primary-foreground/70 text-sm">Based & Focused</p>
              </div>
              <div className="bg-primary-foreground/10 rounded-xl p-6 text-center">
                <p className="font-heading text-4xl font-bold mb-2">2</p>
                <p className="text-primary-foreground/70 text-sm">Week Average Build</p>
              </div>
              <div className="bg-primary-foreground/10 rounded-xl p-6 text-center">
                <p className="font-heading text-4xl font-bold mb-2">100%</p>
                <p className="text-primary-foreground/70 text-sm">Client Satisfaction</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              We'd love to hear about your business and see how we can help. 
              No pressure, just a friendly chat.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                data-testid="about-bottom-cta-btn"
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
              >
                <Link to="/contact">Get in Touch</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="about-portfolio-btn"
                className="rounded-full px-8 py-6 text-lg border-2"
              >
                <Link to="/portfolio">See Our Work</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
